import { Suspense, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { ErrorBoundary } from '@/components/common/ErrorBoundary/ErrorBoundary';
import { ProtectedRoute } from '@/components/common/ProtectedRoute/ProtectedRoute';
import { LoadingSpinner } from '@/components/common/LoadingSpinner/LoadingSpinner';
import { ToastProvider } from '@/components/ui/toast-provider';
import { Login } from '@/pages/Login';
import { ROUTES } from '@/utils/constants';
import HomePage from '@/pages';
import { ServiceDetails } from '@/pages/Services';
import { MySubscriptions } from '@/pages/MySubscriptions';
import { MyAccount } from '@/pages/MyAccount';
import { UsageDetails } from '@/pages/UsageDetails';
import { BillingHistory } from '@/pages/BillingHistory';
import { Privacy } from '@/pages/Privacy';
import { Terms } from '@/pages/Terms';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { HealthCheck } from '@/components/common/HealthCheck';

function App() {
  const auth = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleAuthError = () => {
      auth.logout();
      navigate(ROUTES.LOGIN);
    };

    window.addEventListener('auth-error', handleAuthError);

    return () => {
      window.removeEventListener('auth-error', handleAuthError);
    };
  }, [auth, navigate]);

  return (
    <ErrorBoundary>
      <ToastProvider>
        <Routes>
          {/* Health check endpoint for GKE */}
          <Route path="/health" element={<HealthCheck />} />
          
          {/* Public routes with shared layout */}
          <Route element={<Layout />}>
            <Route path={ROUTES.HOME} element={<HomePage />} />
            <Route path={ROUTES.SERVICES} element={<HomePage />} />
            <Route path="/services/:id" element={<ServiceDetails />} />
            <Route path={ROUTES.PRIVACY} element={<Privacy />} />
            <Route path={ROUTES.TERMS} element={<Terms />} />
          </Route>
          <Route path={ROUTES.LOGIN} element={
            <ProtectedRoute requireAuth={false}>
              <Login />
            </ProtectedRoute>
          } />

        {/* Protected routes with layout */}
        <Route path="/*" element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<LoadingSpinner size="lg" text="Loading..." />}>
                <Routes>
                  <Route path={ROUTES.DASHBOARD} element={<div className="space-y-6"><h1 className="text-3xl font-bold">Dashboard</h1><p className="text-muted-foreground">Overview of your services and analytics</p></div>} />
                  <Route path={ROUTES.MY_ACCOUNT} element={<MyAccount />} />
                  <Route path={ROUTES.MY_SUBSCRIPTIONS} element={<MySubscriptions />} />
                  <Route path="/my-account/subscriptions/:id/usage" element={<UsageDetails />} />
                  <Route path="/my-account/subscriptions/:id/billing" element={<BillingHistory />} />
                  <Route path="*" element={<div className="min-h-[400px] flex items-center justify-center"><div className="text-center"><h1 className="text-4xl font-bold mb-4">404</h1><p className="text-muted-foreground">Page not found</p></div></div>} />
                </Routes>
              </Suspense>
            </Layout>
          </ProtectedRoute>
        } />
        </Routes>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
